import { useEffect, useState } from 'react';
import type { PlaylistSummary, UserProfile, Video } from '@blackblack/shared';
import { formatCount } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { useFollowing } from '../../lib/entities';
import { setQueryData, useInfinite, useQuery } from '../../lib/query';
import { Link, navigate, useParams, useSearchParams } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Avatar, Button, EmptyState, ErrorState, IconButton, Skeleton, Tabs, VerifiedBadge } from '../../components/ui/primitives';
import { ActionList, Sheet, useOpenSheet } from '../../components/ui/Sheet';
import { Page, PageHeader } from '../../components/layout';
import { FollowButton } from '../../components/UserRow';
import { VideoGrid } from '../../components/VideoGrid';
import { CenteredSpinner } from '../../app/Outlet';
import { PlaylistCard } from '../playlists/PlaylistCard';
import { ReportSheet } from '../moderation/ReportSheet';
import { euro, goToStripe } from '../billing/money';
import styles from './profile.module.css';

type Tab = 'videos' | 'likes' | 'playlists' | 'saved';

/** /me → own profile. */
export function MyProfileRedirect() {
  const { me } = useAuth();
  useEffect(() => {
    if (me) navigate(`/u/${me.username}`, { replace: true });
  }, [me]);
  return <CenteredSpinner />;
}

export function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const { me } = useAuth();
  const key = `profile:${username}:${me?.id ?? 'anon'}`;
  const q = useQuery<UserProfile>(key, () => api.get(`/users/${encodeURIComponent(username)}`));
  const [tab, setTab] = useState<Tab>('videos');
  const p = q.data;
  const params = useSearchParams();
  const toast = useToast();

  // Back from Stripe Checkout after supporting this creator.
  useEffect(() => {
    if (params.get('supported') !== '1') return;
    toast(`Bedankt voor je steun aan @${username}!`, { tone: 'success' });
    setTimeout(() => void q.refetch(), 2500); // the webhook usually lands within a second or two
    navigate(`/u/${username}`, { replace: true });
  }, [params]);

  if (q.error) {
    return (
      <Page>
        <PageHeader />
        <ErrorState error={q.error} onRetry={q.refetch} />
      </Page>
    );
  }

  const tabs: Array<{ id: Tab; label: string; icon: 'grid' | 'heart' | 'music' | 'bookmark' }> = [
    { id: 'videos', label: "Video's", icon: 'grid' },
    { id: 'likes', label: 'Likes', icon: 'heart' },
    { id: 'playlists', label: 'Playlists', icon: 'music' },
    ...(p?.isSelf ? [{ id: 'saved' as Tab, label: 'Opgeslagen', icon: 'bookmark' as const }] : []),
  ];

  return (
    <Page>
      <PageHeader
        title={p ? `@${p.username}` : ''}
        back={!p?.isSelf}
        actions={
          p?.isSelf ? (
            <>
              <IconButton icon="bell" label="Meldingen" onClick={() => navigate('/notifications')} />
              <IconButton icon="settings" label="Instellingen" onClick={() => navigate('/settings')} />
            </>
          ) : (
            p && <ProfileMenu profile={p} onChange={(next) => setQueryData(key, () => next)} />
          )
        }
      />
      {p ? <ProfileHeader profile={p} /> : <HeaderSkeleton />}
      {p && !p.isBlocked && (
        <>
          <Tabs tabs={tabs} value={tab} onChange={setTab} sticky />
          <div className={styles.tabBody}>
            {tab === 'videos' && <UserVideos userId={p.id} isSelf={p.isSelf} />}
            {tab === 'likes' && <LikedVideos userId={p.id} />}
            {tab === 'playlists' && <UserPlaylists userId={p.id} isSelf={p.isSelf} />}
            {tab === 'saved' && <SavedVideos />}
          </div>
        </>
      )}
      {p?.isBlocked && <EmptyState icon="ban" title="Je hebt dit account geblokkeerd" text="Deblokkeer via het menu om hun content weer te zien." />}
    </Page>
  );
}

function ProfileHeader({ profile: p }: { profile: UserProfile }) {
  const following = useFollowing(p.id, p.isFollowing);
  const followers = p.followerCount + (following === p.isFollowing ? 0 : following ? 1 : -1);
  return (
    <section className={styles.header}>
      <Avatar user={p} size={96} ring={p.verified} />
      <h1 className={styles.name}>
        {p.displayName} {p.verified && <VerifiedBadge size={18} />}
        {p.pro && <span className={styles.proBadge} title="Creator Pro">PRO</span>}
      </h1>
      <div className={styles.stats}>
        <Link to={`/u/${p.username}/following`}>
          <strong>{formatCount(p.followingCount)}</strong>
          <span>Volgend</span>
        </Link>
        <Link to={`/u/${p.username}/followers`}>
          <strong>{formatCount(followers)}</strong>
          <span>Volgers</span>
        </Link>
        <span>
          <strong>{formatCount(p.likeCount)}</strong>
          <span>Likes</span>
        </span>
      </div>
      <div className={styles.actions}>
        {p.isSelf ? (
          <>
            <Button variant="secondary" onClick={() => navigate('/settings/profile')}>
              Profiel bewerken
            </Button>
            <Button
              variant="secondary"
              icon="share"
              aria-label="Profiel delen"
              onClick={() => void navigator.clipboard?.writeText(`${location.origin}/u/${p.username}`)}
            />
          </>
        ) : p.isBlocked ? null : (
          <>
            <FollowButton userId={p.id} initial={p.isFollowing} size="md" />
            <SupportButton profile={p} />
          </>
        )}
      </div>
      {p.support.supporterCount > 0 && (
        <p className={styles.supporters}>
          <Icon name="heart" size={14} filled /> {formatCount(p.support.supporterCount)} {p.support.supporterCount === 1 ? 'supporter' : 'supporters'}
        </p>
      )}
      {p.bio && <p className={styles.bio}>{p.bio}</p>}
    </section>
  );
}

/** Monthly fan support via Stripe Checkout; shown when the creator has it switched on. */
function SupportButton({ profile: p }: { profile: UserProfile }) {
  const { requireAuth } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (p.support.isSupporting) {
    return (
      <Button variant="secondary" icon="heart" onClick={() => navigate('/settings/billing')}>
        Je steunt
      </Button>
    );
  }
  if (!p.support.enabled || p.support.priceCents === null) return null;
  const start = async () => {
    if (!requireAuth(`Log in om @${p.username} te steunen`)) return;
    setBusy(true);
    try {
      goToStripe((await api.post<{ url: string }>(`/users/${p.id}/support/checkout`)).url);
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" icon="heart" loading={busy} onClick={() => void start()}>
      Steun {euro(p.support.priceCents)}/mnd
    </Button>
  );
}

function ProfileMenu({ profile: p, onChange }: { profile: UserProfile; onChange: (p: UserProfile) => void }) {
  const openSheet = useOpenSheet();
  const { requireAuth } = useAuth();
  const toast = useToast();
  const toggleBlock = async () => {
    if (!requireAuth()) return;
    if (!p.isBlocked && !confirm(`@${p.username} blokkeren? Jullie zien elkaars content niet meer en volgen elkaar niet meer.`)) return;
    try {
      if (p.isBlocked) await api.delete(`/users/${p.id}/block`);
      else await api.post(`/users/${p.id}/block`);
      onChange({ ...p, isBlocked: !p.isBlocked, isFollowing: false });
      toast(p.isBlocked ? 'Gedeblokkeerd' : 'Geblokkeerd');
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    }
  };
  return (
    <IconButton
      icon="more"
      label="Meer"
      onClick={() =>
        openSheet((close) => (
          <Sheet open onClose={close} title={`@${p.username}`}>
            <ActionList
              actions={[
                { label: 'Profiellink kopiëren', icon: <Icon name="link" size={20} />, onClick: () => (close(), void navigator.clipboard?.writeText(`${location.origin}/u/${p.username}`), toast('Link gekopieerd')) },
                { label: p.isBlocked ? 'Deblokkeren' : 'Blokkeren', icon: <Icon name="ban" size={20} />, danger: !p.isBlocked, onClick: () => (close(), void toggleBlock()) },
                { label: 'Rapporteren', icon: <Icon name="flag" size={20} />, danger: true, onClick: () => (close(), requireAuth() && openSheet((c) => <ReportSheet targetType="user" targetId={p.id} onClose={c} />)) },
              ]}
            />
          </Sheet>
        ))
      }
    />
  );
}

function UserVideos({ userId, isSelf }: { userId: string; isSelf: boolean }) {
  const list = useInfinite<Video>(`user-videos:${userId}`, (cursor) => api.page<Video>(`/users/${userId}/videos`, { cursor, limit: 24 }));
  return (
    <VideoGrid
      items={list.items}
      loading={list.initialLoading}
      hasMore={list.hasMore}
      loadingMore={list.loading}
      onLoadMore={list.loadMore}
      badge={(v) => (v.visibility !== 'public' ? <span className={styles.privBadge}><Icon name={v.visibility === 'private' ? 'lock' : 'users'} size={12} /></span> : null)}
      empty={
        isSelf ? (
          <EmptyState icon="camera" title="Deel je eerste video" text="Je video's verschijnen hier." action={<Button icon="plus" onClick={() => navigate('/create')}>Video maken</Button>} />
        ) : (
          <EmptyState icon="grid" title="Nog geen video's" />
        )
      }
    />
  );
}

function LikedVideos({ userId }: { userId: string }) {
  const list = useInfinite<Video>(`user-likes:${userId}`, (cursor) => api.page<Video>(`/users/${userId}/liked-videos`, { cursor, limit: 24 }));
  return <VideoGrid items={list.items} loading={list.initialLoading} hasMore={list.hasMore} loadingMore={list.loading} onLoadMore={list.loadMore} empty={<EmptyState icon="heart" title="Nog geen gelikete video's" />} />;
}

function SavedVideos() {
  const { me } = useAuth();
  const list = useInfinite<Video>(`saved:${me?.id}`, (cursor) => api.page<Video>('/users/me/saved-videos', { cursor, limit: 24 }));
  return (
    <VideoGrid
      items={list.items}
      loading={list.initialLoading}
      hasMore={list.hasMore}
      loadingMore={list.loading}
      onLoadMore={list.loadMore}
      empty={<EmptyState icon="bookmark" title="Nog niets opgeslagen" text="Tik op het bladwijzer-icoon bij een video om hem hier te bewaren. Alleen jij ziet dit." />}
    />
  );
}

function UserPlaylists({ userId, isSelf }: { userId: string; isSelf: boolean }) {
  const q = useQuery<PlaylistSummary[]>(`playlists:user:${userId}`, () => api.get(`/users/${userId}/playlists`));
  if (q.loading) return <div className={styles.playlistGrid}>{[0, 1, 2].map((i) => <Skeleton key={i} w={148} h={190} r={14} />)}</div>;
  if (!q.data?.length) return <EmptyState icon="music" title={isSelf ? 'Nog geen playlists' : 'Geen openbare playlists'} action={isSelf && <Button onClick={() => navigate('/music')}>Naar muziek</Button>} />;
  return (
    <div className={styles.playlistGrid}>
      {q.data.map((p) => (
        <PlaylistCard key={p.id} playlist={p} />
      ))}
    </div>
  );
}

function HeaderSkeleton() {
  return (
    <section className={styles.header}>
      <Skeleton w={96} h={96} r={48} />
      <Skeleton w={160} h={22} />
      <Skeleton w={220} h={40} />
    </section>
  );
}
